import { Controller } from '@hotwired/stimulus';
import describeBy from 'stimulus/describe_by';

// Show/hide for a password and the hint that only shows while the field is focused and the
// password is still too short.

export default class extends Controller {
  static targets = ['input', 'show', 'hide', 'message'];

  static values = {
    minLength: { type: Number, default: 8 },
    messageId: String,
    visible: Boolean
  };

  connect() {
    this.focused = false;
    // Turbo snapshots the page as-is; a revealed password must not come back revealed.
    this.handleBeforeCache = () => { this.visibleValue = false; };
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);
    this.syncHint();
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
  }

  // ── Public actions (wired from data-action) ────────────────────────────────
  toggle() {
    this.visibleValue = !this.visibleValue;
    // The clicked button is now hidden, so move focus to the one replacing it.
    this.activeToggle.focus();
  }

  focus() {
    this.focused = true;
    this.syncHint();
  }

  blur() {
    this.focused = false;
    this.syncHint();
  }

  sync() {
    this.syncHint();
  }

  // ── Visibility ────────────────────────────────────────────────────────────
  visibleValueChanged(visible) {
    if (!this.hasInputTarget) return;

    this.inputTarget.type = visible ? 'text' : 'password';
    if (!this.hasShowTarget) return;

    this.showTarget.hidden = visible;
    this.hideTarget.hidden = !visible;
  }

  get activeToggle() {
    return this.visibleValue ? this.hideTarget : this.showTarget;
  }

  // ── Hint ──────────────────────────────────────────────────────────────────
  // An error owns the message region while it is showing, so leave it untouched until it is
  // cleared.
  syncHint() {
    if (!this.hasMessageTarget || this.errorVisible) return;

    const show = this.focused && !this.meetsRequirements && this.hintText !== '';
    this.messageTarget.hidden = !show;
    this.describeByMessage(show);
  }

  get errorVisible() {
    return this.element.classList.contains('ui-field--error');
  }

  get hintText() {
    return this.element.dataset.inputFieldHint || '';
  }

  get meetsRequirements() {
    return this.inputTarget.value.length >= this.minLengthValue;
  }

  describeByMessage(present) {
    describeBy(this.inputTarget, this.messageIdValue, present);
  }
}
